import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieConsent = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always required
        functional: true,
        analytics: true,
        marketing: false,
    });

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            // Delay showing the banner for better UX
            const timer = setTimeout(() => setShowBanner(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const acceptAll = () => {
        const allAccepted = {
            necessary: true,
            functional: true,
            analytics: true,
            marketing: true,
            timestamp: new Date().toISOString(),
        };
        localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
        setShowBanner(false);
    };

    const acceptSelected = () => {
        const selected = {
            ...preferences,
            timestamp: new Date().toISOString(),
        };
        localStorage.setItem('cookieConsent', JSON.stringify(selected));
        setShowBanner(false);
        setShowSettings(false);
    };

    const rejectNonEssential = () => {
        const essentialOnly = {
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false,
            timestamp: new Date().toISOString(),
        };
        localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
        setShowBanner(false);
    };

    if (!showBanner) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
                    {/* Main Banner */}
                    {!showSettings ? (
                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                                    <Cookie className="w-6 h-6 text-primary-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                                        We value your privacy
                                    </h3>
                                    <p className="text-sm text-neutral-600 mb-4">
                                        We use cookies to enhance your browsing experience, analyse site traffic, and personalise content. 
                                        By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or 
                                        learn more in our{' '}
                                        <Link to="/privacy" className="text-primary-600 hover:underline">
                                            Privacy Policy
                                        </Link>.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <Button 
                                            onClick={acceptAll}
                                            className="bg-primary-600 hover:bg-primary-700"
                                        >
                                            <Check className="w-4 h-4 mr-2" />
                                            Accept All
                                        </Button>
                                        <Button 
                                            variant="outline"
                                            onClick={rejectNonEssential}
                                        >
                                            Reject Non-Essential
                                        </Button>
                                        <Button 
                                            variant="ghost"
                                            onClick={() => setShowSettings(true)}
                                            className="text-neutral-600"
                                        >
                                            <Settings className="w-4 h-4 mr-2" />
                                            Cookie Settings
                                        </Button>
                                    </div>
                                </div>
                                <button
                                    onClick={rejectNonEssential}
                                    className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-neutral-400" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Settings Panel */
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-neutral-900">
                                    Cookie Preferences
                                </h3>
                                <button
                                    onClick={() => setShowSettings(false)}
                                    className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-neutral-400" />
                                </button>
                            </div>

                            <div className="space-y-4 mb-6">
                                {/* Necessary Cookies */}
                                <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl">
                                    <div>
                                        <h4 className="font-medium text-neutral-900">Necessary Cookies</h4>
                                        <p className="text-sm text-neutral-500">Required for the website to function properly</p>
                                    </div>
                                    <div className="px-3 py-1 bg-neutral-200 text-neutral-600 text-sm rounded-full">
                                        Always Active
                                    </div>
                                </div>

                                {/* Functional Cookies */}
                                <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl">
                                    <div>
                                        <h4 className="font-medium text-neutral-900">Functional Cookies</h4>
                                        <p className="text-sm text-neutral-500">Remember your preferences and settings</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={preferences.functional}
                                            onChange={(e) => setPreferences({...preferences, functional: e.target.checked})}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>

                                {/* Analytics Cookies */}
                                <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl">
                                    <div>
                                        <h4 className="font-medium text-neutral-900">Analytics Cookies</h4>
                                        <p className="text-sm text-neutral-500">Help us understand how visitors use our site</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={preferences.analytics}
                                            onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>

                                {/* Marketing Cookies */}
                                <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl">
                                    <div>
                                        <h4 className="font-medium text-neutral-900">Marketing Cookies</h4>
                                        <p className="text-sm text-neutral-500">Used for targeted advertising and promotions</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={preferences.marketing}
                                            onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Button 
                                    onClick={acceptSelected}
                                    className="bg-primary-600 hover:bg-primary-700"
                                >
                                    Save Preferences
                                </Button>
                                <Button 
                                    variant="outline"
                                    onClick={acceptAll}
                                >
                                    Accept All
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
